import { extractYouTubeVideoId, toYouTubeWatchUrl } from '../utils/youtubeEmbed';

export type NoembedYouTubeResponse = {
  provider_url?: string;
  thumbnail_url?: string;
  thumbnail_width?: number;
  thumbnail_height?: number;
  author_url?: string;
  author_name?: string;
  title?: string;
  url?: string;
  provider_name?: string;
  width?: number;
  height?: number;
  version?: string;
  html?: string;
  type?: string;
  error?: string;
};

const cache = new Map<string, NoembedYouTubeResponse>();

/**
 * Mag Academy–style metadata fetch via noembed oEmbed proxy.
 * @see https://noembed.com/embed?url=https://www.youtube.com/watch?v=VIDEO_ID
 */
export async function fetchYouTubeNoembed(
  videoUrlOrId: string
): Promise<NoembedYouTubeResponse | null> {
  const watchUrl = toYouTubeWatchUrl(videoUrlOrId);
  if (!watchUrl) return null;

  const cached = cache.get(watchUrl);
  if (cached) return cached;

  const videoId = extractYouTubeVideoId(videoUrlOrId);
  const fallback: NoembedYouTubeResponse | null = videoId
    ? {
        url: watchUrl,
        title: '',
        thumbnail_url: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
        provider_name: 'YouTube',
        type: 'video',
      }
    : null;

  try {
    const endpoint = `https://noembed.com/embed?url=${encodeURIComponent(watchUrl)}`;
    const response = await fetch(endpoint);
    if (!response.ok) return fallback;

    const data = (await response.json()) as NoembedYouTubeResponse;
    if (data.error) return fallback;

    const normalized: NoembedYouTubeResponse = {
      ...data,
      thumbnail_url:
        data.thumbnail_url ||
        (videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : undefined),
    };

    cache.set(watchUrl, normalized);
    return normalized;
  } catch {
    return fallback;
  }
}
